import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserRound, Building2, Loader2, X, ChevronRight, Lock } from 'lucide-react';
import { fmtMoney } from '@/lib/format';

/*
  Vía de pago antes de ir a Stripe (decisión de Samuel, 09/10/2026):
    - «Particular» → Stripe de Freedom Life Club LLC, sin factura ni IVA.
    - «Empresa o autónomo» → Stripe de Inbrindor Tech, S.L., 21 % de IVA y factura (solo pagos en euros).
  El backend decide qué vías hay (membership.billing / events.billing): aquí solo se muestran.
  Upgrades y plaza vitalicia de quien ya tiene una anual se cobran por la vía de esa suscripción:
  no se ofrece elegir, solo se informa (LockedChannelNote).
*/

const CHANNEL_LABEL = { particular: 'Particular', empresa: 'Empresa o autónomo' };

// Importe con impuesto, redondeado al céntimo como Stripe (impuesto exclusivo por línea)
const withVat = (amount, pct) => {
  const cents = Math.round(Number(amount || 0) * 100);
  return (cents + Math.round((cents * Number(pct || 0)) / 100)) / 100;
};
const pctLabel = (pct) => `${String(pct).replace('.', ',')} %`;

// «449 €» o «449 € + 21 % IVA = 543,29 €»
export function priceWithVat(amount, currency, vatPct) {
  if (!vatPct) return fmtMoney(amount, currency);
  return `${fmtMoney(amount, currency)} + ${pctLabel(vatPct)} IVA = ${fmtMoney(withVat(amount, vatPct), currency)}`;
}

// ¿Hay que preguntar la vía? Solo en euros, con el selector activo y sin suscripción que fije la vía.
export function needsBillingChoice(billing, currency, locked = false) {
  return !!billing?.show_picker && String(currency || billing?.currency || '').toLowerCase() === 'eur' && !locked;
}

// Vía fijada por la suscripción anual vigente (upgrades y vitalicia), o null
export function lockedChannel(billing) {
  return billing?.subscription_channel || null;
}

// Aviso de la vía con la que se cobrará un upgrade o la vitalicia
export function LockedChannelNote({ billing, amount, currency, className = '' }) {
  const ch = lockedChannel(billing);
  if (!ch) return null;
  const pct = billing.subscription_vat_pct || 0;
  return (
    <p className={`text-xs text-gray-400 leading-relaxed ${className}`} data-testid="billing-locked-note">
      <Lock size={11} className="inline -mt-0.5 mr-1 text-gray-400" aria-hidden="true" />
      Se cobra como {CHANNEL_LABEL[ch]?.toLowerCase() || ch}, igual que tu membresía actual
      {pct ? `: ${priceWithVat(amount, currency, pct)}.` : '.'}
      {ch === 'empresa' && billing.subscription_entity ? ` Factura emitida por ${billing.subscription_entity}.` : ''}
    </p>
  );
}

function Option({ channel, amount, currency, period, busy, disabled, onSelect, autoFocus }) {
  const Icon = channel.id === 'empresa' ? Building2 : UserRound;
  const unavailable = !channel.available;
  const vat = channel.vat_pct || 0;
  return (
    <button
      type="button"
      autoFocus={autoFocus}
      onClick={() => onSelect(channel.id)}
      disabled={disabled || unavailable}
      className={`group w-full text-left rounded-xl border px-4 py-4 min-h-[64px] flex items-center gap-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]/60 ${
        unavailable
          ? 'border-white/[0.05] bg-white/[0.01] cursor-not-allowed'
          : 'border-white/[0.08] bg-white/[0.02] hover:border-[#c9a84c]/40 hover:bg-[#c9a84c]/[0.05] disabled:opacity-60'
      }`}
      data-testid={`billing-option-${channel.id}`}
    >
      <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${
        unavailable ? 'border-white/[0.06] bg-white/[0.02]' : 'border-[#c9a84c]/30 bg-[#c9a84c]/10'}`}>
        <Icon size={18} className={unavailable ? 'text-gray-500' : 'text-[#c9a84c]'} />
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-sm font-semibold ${unavailable ? 'text-gray-400' : 'text-white'}`}>
          {channel.id === 'empresa' ? 'Empresa o autónomo (factura con IVA)' : 'Particular'}
        </span>
        {unavailable ? (
          <span className="block text-xs text-gray-400 mt-0.5">{channel.unavailable_reason || 'Disponible próximamente'}</span>
        ) : (
          <>
            <span className="block text-sm text-gray-200 mt-0.5 tabular-nums break-words">
              {priceWithVat(amount, currency, vat)}{period ? <span className="text-gray-400"> {period}</span> : null}
            </span>
            {channel.invoice && (
              <span className="block text-xs text-gray-400 mt-0.5">Factura emitida por {channel.entity}</span>
            )}
          </>
        )}
      </span>
      {!unavailable && (
        busy ? <Loader2 size={16} className="animate-spin text-[#c9a84c] flex-shrink-0" />
          : <ChevronRight size={16} className="text-gray-500 group-hover:text-[#c9a84c] flex-shrink-0" />
      )}
    </button>
  );
}

/**
 * Modal «¿Cómo quieres pagar?».
 * amount: importe sin impuestos en unidades (449, no 44900) · currency: 'eur' | 'usd'
 * period: texto tras el precio (p. ej. «/ año») · busyChannel: vía que está abriendo Stripe
 */
export function BillingChannelPicker({ open, billing, amount, currency, period, title, subtitle, busyChannel, onSelect, onClose }) {
  const panel = useRef(null);
  const isEur = String(currency || '').toLowerCase() === 'eur';
  const channels = (billing?.channels || []).filter((c) => c.id === 'particular' || isEur);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busyChannel) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busyChannel, onClose]);

  // Portal a <body>: un ancestro con transform (tarjetas animadas) rompería el position: fixed
  if (typeof document === 'undefined') return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog" aria-modal="true" aria-labelledby="billing-title"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busyChannel) onClose?.(); }}
          data-testid="billing-picker"
        >
          <motion.div
            ref={panel}
            initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="relative w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl border border-white/[0.08] bg-[#0a1225] p-6 sm:p-7 shadow-2xl"
          >
            <button type="button" onClick={onClose} disabled={!!busyChannel} aria-label="Cerrar"
              className="absolute top-3 right-3 w-10 h-10 inline-flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/[0.04] disabled:opacity-40">
              <X size={18} />
            </button>
            <p className="text-[11px] uppercase tracking-wider text-[#c9a84c] font-semibold mb-2">Datos de facturación</p>
            <h2 id="billing-title" className="text-white text-xl font-semibold leading-snug pr-8">{title || '¿Cómo quieres pagar?'}</h2>
            {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
            <div className="mt-5 space-y-3">
              {channels.map((ch, i) => (
                <Option key={ch.id} channel={ch} amount={amount} currency={currency} period={period}
                  busy={busyChannel === ch.id} disabled={!!busyChannel} onSelect={onSelect} autoFocus={i === 0} />
              ))}
            </div>
            <p className="text-xs text-gray-400 leading-relaxed mt-5">
              El pago se completa de forma segura en Stripe.
              {isEur && channels.some((c) => c.id === 'empresa' && c.available)
                ? ' Si pagas como empresa o autónomo, allí te pediremos la razón social, el NIF y la dirección fiscal para emitir la factura.'
                : ''}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export default BillingChannelPicker;
